import type { PromptInDb, EnumResponseType } from '@/api';

/**
 * Validation utilities for filter values based on column response types
 *
 * Extract the response_type from API types, excluding EnumResponseType objects
 * (filters only work with string literal types, not enum objects)
 */
export type ResponseType = Exclude<PromptInDb['response_type'], EnumResponseType>;

/**
 * String response types that support substring matching (contains operation)
 * Extracted from ResponseType to ensure type safety
 */
type StringResponseType = Extract<ResponseType, 'str'>;

export type ValidationResult =
  { valid: true; parsedValue: string | number | boolean } | { valid: false; error: string };

/**
 * Extract the element type from a list response type
 * e.g., 'list[str]' -> 'str'
 * Note: CalendarEvents is inherently a list type (array of CalendarEvent)
 * so we treat it as list[CalendarEvent] - extract CalendarEvent (singular) as element type
 */
export function getElementType(responseType: ResponseType): ResponseType {
  if (responseType.startsWith('list[')) {
    return responseType.substring(5, responseType.length - 1) as ResponseType;
  }
  // CalendarEvents is a list type (array of CalendarEvent objects)
  // When extracting element type, return CalendarEvent (singular) to match the pattern
  // of list[Entity] -> Entity, list[CurrencyAmount] -> CurrencyAmount
  if (responseType === 'CalendarEvents') {
    return 'CalendarEvent' as ResponseType;
  }
  return responseType;
}

/**
 * Validate a single value based on response type
 */
export function validateFilterValue(
  value: string | number | null | undefined,
  responseType: ResponseType
): ValidationResult {
  if (value === null || value === undefined || value === '') {
    return { valid: false, error: 'Value cannot be empty' };
  }

  const stringValue = String(value).trim();

  switch (responseType) {
    case 'int':
      return validateInteger(stringValue);

    case 'float':
    case 'number':
      return validateNumber(stringValue);

    case 'bool':
      return validateBoolean(stringValue);

    case 'date':
    case 'datetime':
      return validateDateTime(stringValue);

    default:
      // String types are always valid
      return { valid: true, parsedValue: stringValue };
  }
}

/**
 * Validate multiple values (for Contains/IN operations) based on response type
 */
export function validateFilterValues(
  values: (string | number)[],
  responseType: ResponseType
): ValidationResult {
  if (values.length === 0) {
    return { valid: false, error: 'At least one value is required' };
  }

  const errors: string[] = [];
  const parsedValues: (string | number | boolean)[] = [];

  for (const value of values) {
    const result = validateFilterValue(value, responseType);
    if (result.valid) {
      parsedValues.push(result.parsedValue);
    } else {
      errors.push(result.error || 'Invalid value');
    }
  }

  if (errors.length > 0) {
    return {
      valid: false,
      error: errors.length === 1 ? errors[0] : `Multiple errors: ${errors.join('; ')}`,
    };
  }

  return { valid: true, parsedValue: parsedValues.join(',') };
}

/**
 * Validate integer value
 */
function validateInteger(value: string): ValidationResult {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { valid: false, error: 'Number cannot be empty' };
  }

  // Check if it's a valid integer (allows negative numbers)
  if (!/^-?\d+$/.test(trimmed)) {
    return { valid: false, error: 'Must be a whole number (e.g., 42, -10)' };
  }

  const parsed = parseInt(trimmed, 10);
  if (isNaN(parsed)) {
    return { valid: false, error: 'Invalid number format' };
  }

  return { valid: true, parsedValue: parsed };
}

/**
 * Validate number (float) value
 */
function validateNumber(value: string): ValidationResult {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { valid: false, error: 'Number cannot be empty' };
  }

  // Check if it's a valid number (allows decimals and negative)
  if (!/^-?\d*\.?\d+$/.test(trimmed)) {
    return { valid: false, error: 'Must be a number (e.g., 42, -10.5, 3.14)' };
  }

  const parsed = parseFloat(trimmed);
  if (isNaN(parsed)) {
    return { valid: false, error: 'Invalid number format' };
  }

  return { valid: true, parsedValue: parsed };
}

/**
 * Validate boolean value
 */
function validateBoolean(value: string): ValidationResult {
  const trimmed = value.trim().toLowerCase();
  const validValues = ['true', 'false', 'yes', 'no', '1', '0', 'y', 'n'];

  if (!validValues.includes(trimmed)) {
    return {
      valid: false,
      error: 'Must be yes or no (you can also use true/false, 1/0, y/n)',
    };
  }

  // Convert to boolean
  const boolValue = trimmed === 'true' || trimmed === 'yes' || trimmed === '1' || trimmed === 'y';

  return { valid: true, parsedValue: boolValue };
}

/**
 * Validate datetime value
 */
function validateDateTime(value: string): ValidationResult {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { valid: false, error: 'Date and time cannot be empty' };
  }

  // Try to parse as ISO date string
  const date = new Date(trimmed);
  if (isNaN(date.getTime())) {
    return {
      valid: false,
      error: 'Must be a valid date and time (e.g., 2024-01-15 or 2024-01-15T14:30:00)',
    };
  }

  return { valid: true, parsedValue: trimmed };
}

/**
 * Get input type for HTML input element based on response type
 */
export function getInputType(responseType: ResponseType): string {
  const type = getElementType(responseType);
  switch (type) {
    case 'int':
    case 'float':
    case 'number':
      return 'number';
    case 'bool':
      return 'text'; // We'll use a select for bool
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime-local';
    default:
      return 'text';
  }
}

/**
 * Get placeholder text based on response type
 */
export function getPlaceholder(responseType: ResponseType, isMultiple: boolean): string {
  // Map base element types to placeholder text
  const elementTypePlaceholders: Record<string, string> = {
    str: 'Enter text',
    int: 'Enter number',
    float: 'Enter number',
    number: 'Enter number',
    bool: 'Select yes or no',
    date: 'Enter date',
    datetime: 'Enter date and time',
    CurrencyAmount: 'Enter amount',
    Entity: 'Enter name',
    CalendarEvent: 'Enter calendar events',
  };

  // Get the base element type (handles both simple types and list types)
  const elementType = getElementType(responseType);
  const placeholder =
    elementTypePlaceholders[elementType] ||
    (responseType === 'CalendarEvents' ? 'Enter calendar events' : 'Enter value');

  return isMultiple ? `${placeholder} (separate multiple values with commas)` : placeholder;
}

/**
 * Check if a response type supports the "contains" (substring) operation
 * Only string types support contains operation
 */
export function supportsContainsOperation(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  // Check if responseType is a string response type (extracted from API types)
  const stringResponseType: StringResponseType = 'str';
  return responseType === stringResponseType;
}

/**
 * Check if a response type supports comparison operations (>, >=, <, <=)
 * Numeric types (int, float, number) and datetime support comparison operations
 */
export function supportsComparisonOperations(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  const baseType = getElementType(responseType);
  return (
    baseType === 'int' ||
    baseType === 'float' ||
    baseType === 'number' ||
    baseType === 'date' ||
    baseType === 'datetime'
  );
}

/**
 * Check if a response type supports list operations (list_any, list_all)
 * List types support list operations
 * Note: CalendarEvents is inherently a list type, so it supports list operations
 */
export function supportsListOperations(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  // CalendarEvents is inherently a list type (array of CalendarEvent)
  if (responseType === 'CalendarEvents') return true;
  return responseType.startsWith('list[');
}

/**
 * Check if a response type supports CurrencyAmountCriterion
 */
export function supportsCurrencyAmountCriterion(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  const baseType = getElementType(responseType);
  return baseType === 'CurrencyAmount';
}

/**
 * Check if a response type supports EntityCriterion
 */
export function supportsEntityCriterion(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  const baseType = getElementType(responseType);
  return baseType === 'Entity';
}

/**
 * Check if a response type supports CalendarEventCriterion
 */
export function supportsCalendarEventCriterion(responseType?: ResponseType): boolean {
  if (!responseType) return false;
  // CalendarEvents is the response type, CalendarEvent (singular) is the element type
  if (responseType === 'CalendarEvents') return true;
  const baseType = getElementType(responseType);
  // CalendarEvent (singular) is the element type extracted from CalendarEvents
  // Use string comparison since CalendarEvent is not in ResponseType union but is returned by getElementType
  return (baseType as string) === 'CalendarEvent';
}

/**
 * Get all available filter operations for a given response type
 * Returns an array of operation keys that can be used to create filter rules
 *
 * @param responseType - The response type (string literal, excludes EnumResponseType)
 * @param enumResponseType - Optional EnumResponseType object. If provided and multiple_choices is true,
 *                           the field is treated as a list type and only list_any/list_all operations are returned
 */
export type FilterOperation =
  | 'equals'
  | 'contains'
  | 'greater_than'
  | 'greater_equal'
  | 'less_than'
  | 'less_equal'
  | 'list_any'
  | 'list_all'
  | 'currency_amount'
  | 'entity'
  | 'calendar_event';

export function getAvailableFilterOperations(
  responseType?: ResponseType,
  enumResponseType?: EnumResponseType
): FilterOperation[] {
  // Check if this is a multiple choice enum (treat as list type)
  if (enumResponseType?.multiple_choices === true) {
    // Multiple choice enums are list types - only show list_any and list_all
    return ['list_any', 'list_all'];
  }

  if (!responseType) return ['equals'];

  const operations: FilterOperation[] = [];

  const isCurrency = supportsCurrencyAmountCriterion(responseType);
  const isEntity = supportsEntityCriterion(responseType);
  const isCalendar = supportsCalendarEventCriterion(responseType);
  const isList = supportsListOperations(responseType);
  const hasSpecialCriterion = isCurrency || isEntity || isCalendar;

  // 1. Handle non-list types
  if (isList) {
    // 2. Handle list types
    // For ALL list types, only show list_any and list_all
    // Users must select list_any or list_all first, then add operations for the element type inside
    operations.push('list_any', 'list_all');
  } else {
    if (hasSpecialCriterion) {
      // Special complex types (not list) - only show that type
      if (isCurrency) operations.push('currency_amount');
      else if (isEntity) operations.push('entity');
      else if (isCalendar) operations.push('calendar_event');
    } else {
      // Standard types (not list)
      operations.push('equals');
      if (supportsContainsOperation(responseType)) {
        operations.push('contains');
      }
      if (supportsComparisonOperations(responseType)) {
        operations.push('greater_than', 'greater_equal', 'less_than', 'less_equal');
      }
    }
  }

  // Fallback if nothing was added
  if (operations.length === 0) {
    operations.push('equals');
  }

  return operations;
}
